import { WorkInterval } from '@chilipiper/api-client-user-availability'
import { Flex, Grid, Text } from '@chilipiper/design-system'
import { IconButton, IconTooltip, Select } from '@chilipiper/design-system/src/new'
import { SvgAdd, SvgRemove } from '@chilipiper/icons/src/design-system'
import { SvgCopy } from '@chilipiper/icons/src/design-system'
import { getLocale } from '@chilipiper/locale'
import React from 'react'
import {
  addDurationToTimeString,
  calculateChangedEndTime,
  calculateChangedStartTimeDifference,
  every15Minutes,
} from '@/components/availability-schedules/working-hours/utils'

const TIMEPICKER_WIDTH = '136px'
const locale = getLocale().replace('_', '-')

export const WorkingHoursSlot = ({
  workInterval,
  isFirst,
  isOverlapping,
  onAddSlot,
  onChangeStart,
  onCopyIntervals,
  onRemoveSlot,
}: {
  isFirst?: boolean
  isOverlapping?: boolean
  onAddSlot: () => void
  onChangeStart: (startTime: string, updatedDuration: string) => void
  onCopyIntervals: () => void
  onRemoveSlot: () => void
  workInterval: WorkInterval
}) => {
  const [startHour, startMinutes] = workInterval.startsAt.split(':')

  const endTimeString = addDurationToTimeString(
    workInterval.startsAt,
    parseInt(workInterval.duration)
  )
  const [endHour, endMinutes] = endTimeString.split(':')
  const startPickerValue = `${startHour}:${startMinutes}`
  const endPickerValue = `${endHour}:${endMinutes}`

  return (
    <Flex gap={4}>
      <Grid
        alignItems='center'
        gap={2}
        gridTemplateColumns={`${TIMEPICKER_WIDTH} auto ${TIMEPICKER_WIDTH}`}
      >
        <Select
          label={<Select.Label position='hidden'>Working hours start time</Select.Label>}
          variant='ghost'
          onChange={newStartTime => {
            if (!newStartTime) {
              return
            }
            const difference = calculateChangedStartTimeDifference(newStartTime, endPickerValue)
            onChangeStart(newStartTime, `${difference} milliseconds`)
          }}
          value={startPickerValue}
        >
          {every15Minutes('start').map(time => {
            const localeTime = new Date(`2024-01-01T${time}:00Z`).toLocaleTimeString(locale, {
              hour: 'numeric',
              minute: 'numeric',
              timeZone: 'UTC',
            })
            return (
              <Select.Item key={time} value={time}>
                {localeTime}
              </Select.Item>
            )
          })}
        </Select>

        <Text textStyle='item-unselected' color='fg/major'>
          to
        </Text>

        <Select
          label={<Select.Label position='hidden'>Working hours end time</Select.Label>}
          variant='ghost'
          onChange={newEndTime => {
            if (!newEndTime) {
              return
            }
            const { newStartTime, difference } = calculateChangedEndTime(
              startPickerValue,
              newEndTime
            )
            onChangeStart(newStartTime, `${difference} milliseconds`)
          }}
          value={endPickerValue}
        >
          {every15Minutes('end').map(time => {
            const localeTime = new Date(`2024-01-01T${time}:00Z`).toLocaleTimeString(locale, {
              hour: 'numeric',
              minute: 'numeric',
              timeZone: 'UTC',
            })
            return (
              <Select.Item key={time} value={time}>
                {localeTime}
              </Select.Item>
            )
          })}
        </Select>
      </Grid>

      <Flex alignItems='center' justifyContent='start'>
        {isFirst ? (
          <Flex gap={2} alignItems='center'>
            <IconButton
              icon={SvgAdd}
              onClick={onAddSlot}
              label='Add interval'
              size={6}
              variant='ghost'
            />
            <IconButton
              icon={SvgCopy}
              onClick={onCopyIntervals}
              label='Copy to all'
              size={6}
              variant='ghost'
            />
            {isOverlapping && (
              <IconTooltip
                content='Working hours intervals should not overlap'
                variant='error'
                size={4}
                placement='right'
              />
            )}
          </Flex>
        ) : (
          <Flex gap={2} alignItems={'center'}>
            <IconButton
              onClick={onRemoveSlot}
              icon={SvgRemove}
              label='Remove'
              size={6}
              variant='ghost'
            />
            {isOverlapping && (
              <IconTooltip
                content='Working hours intervals should not overlap'
                variant='error'
                size={4}
                ml={1}
                placement='right'
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
